/**
 * Screening Info Requests service — mirrors Angular endpoints.
 * List loads the full config once; search/sort/pagination/reorder-preview are client-side.
 */

import apiClient from '@/lib/api/apiClient'
import { extractErrorMessage } from '@/lib/utils/errorHandling'
import type {
  ScreeningInfoRequest,
  ScreeningInfoRequestCreatePayload,
  ScreeningInfoRequestUpdatePayload,
} from '../types'

const BASE = '/api/screening-info-requests'

/** Backend returns either a bare array or `{ data: [...] }`. */
function normalizeList(data: unknown): ScreeningInfoRequest[] {
  if (Array.isArray(data)) return data as ScreeningInfoRequest[]
  if (data && typeof data === 'object' && Array.isArray((data as { data?: unknown }).data)) {
    return (data as { data: ScreeningInfoRequest[] }).data
  }
  return []
}

export async function getScreeningInfoRequests(): Promise<ScreeningInfoRequest[]> {
  try {
    const response = await apiClient.get(`${BASE}/config`)
    return normalizeList(response.data)
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load messages.')
  }
}

export async function createScreeningInfoRequest(
  payload: ScreeningInfoRequestCreatePayload,
): Promise<void> {
  try {
    await apiClient.post(BASE, payload)
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to save message.')
  }
}

export async function updateScreeningInfoRequest(
  id: number,
  payload: ScreeningInfoRequestUpdatePayload,
): Promise<void> {
  try {
    await apiClient.put(`${BASE}/${id}`, payload)
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to save message.')
  }
}

export async function deleteScreeningInfoRequest(id: number): Promise<void> {
  try {
    await apiClient.delete(`${BASE}/${id}`)
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to remove message.')
  }
}

export async function reorderScreeningInfoRequests(order: number[]): Promise<void> {
  try {
    await apiClient.put(`${BASE}/reorder`, { order })
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to update order.')
  }
}
